/**
 * Shared recharts theming (F-FE-N55).
 *
 * Recharts writes its defaults as INLINE STYLES, which no stylesheet can
 * override. That made it the single loudest dark-mode break in the app and
 * one that no Tailwind grep could ever have found:
 *
 *   - `<Tooltip>` with no `contentStyle` falls back to a `#fff` background
 *     with a `#ccc` border. Thirteen of them across eight report and dashboard
 *     screens, so every hover flashed a white card onto the dark canvas.
 *   - `<XAxis>` / `<YAxis>` ticks default to `#666`, which measures ~2.5:1 on
 *     the dark canvas — a WCAG failure on the labels that say what the numbers
 *     mean.
 *
 * These props are lifted from the two charts that were already correct
 * (dashboard/SalesTimeseriesChart and dashboard/PaymentMixDonut) so there is
 * one definition instead of fourteen, and a future palette change reaches
 * every chart at once.
 *
 * Usage:
 *   <CartesianGrid {...chartGrid} />
 *   <XAxis dataKey="label" {...chartAxis} />
 *   <YAxis {...chartAxis} width={64} tickFormatter={...} />
 *   <Tooltip {...chartTooltip} formatter={...} />
 */

/** Axis ticks + hairlines. Spread first so a caller can still override width, formatter, etc. */
export const chartAxis = {
  tick: { fontSize: 11, fill: 'hsl(var(--muted-foreground))' },
  axisLine: false,
  tickLine: false,
} as const

/** Horizontal-only grid on the border token — visible in every theme, loud in none. */
export const chartGrid = {
  strokeDasharray: '3 3',
  stroke: 'hsl(var(--border))',
  vertical: false,
} as const

/** The floating tooltip card. Sits on --popover, matching every other overlay. */
export const chartTooltip = {
  cursor: { stroke: 'hsl(var(--muted-foreground))', strokeDasharray: '3 3' },
  contentStyle: {
    background: 'hsl(var(--popover))',
    border: '1px solid hsl(var(--border))',
    borderRadius: 8,
    color: 'hsl(var(--popover-foreground))',
    fontSize: 12,
  },
  labelStyle: { color: 'hsl(var(--popover-foreground))' },
  itemStyle: { color: 'hsl(var(--popover-foreground))' },
} as const

/**
 * Bar/pie charts want a filled cursor rather than a crosshair line; a
 * translucent muted block reads as a highlight in both themes, where recharts'
 * default opaque grey swallows the bar underneath it on the dark canvas.
 */
export const chartTooltipBar = {
  ...chartTooltip,
  cursor: { fill: 'hsl(var(--muted-foreground))', fillOpacity: 0.12 },
} as const

/** Pie/donut: no cursor at all. */
export const chartTooltipPie = {
  ...chartTooltip,
  cursor: false,
} as const

export const chartLegend = {
  wrapperStyle: { fontSize: 12, paddingTop: 8, color: 'hsl(var(--muted-foreground))' },
  iconType: 'circle',
} as const

/**
 * Categorical series palette.
 *
 * Deliberately literal hex rather than tokens: a chart series is an identity,
 * not a surface — slice 3 has to stay the same colour when the theme flips, or
 * a reader comparing two screenshots is misled. These are the 500-weight ramp,
 * which clears 3:1 (WCAG 1.4.11, non-text) against both canvases. What they
 * replace is the same list copy-pasted into six files with different orderings.
 */
export const CHART_SERIES = [
  '#0ea5e9', // sky
  '#10b981', // emerald
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#14b8a6', // teal
  '#f97316', // orange
  '#6366f1', // indigo
] as const

export function chartSeriesColor(index: number): string {
  return CHART_SERIES[index % CHART_SERIES.length]
}
